/**
 * The Data import screen (f8.2) -- where the master workbook arrives.
 *
 * This replaces f1.1's phase placeholder behind the `data-import` navigation
 * key, which **f8.2 has now narrowed to Platform Admin** on both sides: the
 * server omits the key from a `USER` session payload, and every endpoint
 * behind this screen refuses a non-Admin, reads included.
 *
 * **The promise the page makes is the feature's defining property.** The file
 * is parsed and staged for review; nothing is written to Atlas on upload, and
 * nothing is written when the parse finishes either. The screen says so, and
 * the only control that could change that -- Commit -- is **f8.3's** and is
 * not drawn here as a disabled button, because a control that does nothing is
 * worse than one that is absent.
 *
 * **What the prototype draws that is deliberately different:**
 *
 * | Prototype | Why | Owner |
 * | --- | --- | --- |
 * | A `SAP ID` column in the expected list | **No sheet in this workbook has one.** `US Projects` has exactly nine headed columns and the string `SAP` appears in no header of any of the three. The prototype's `SAP 21902` values are invention | -- corrected |
 * | *"up to 25 MB"* | The ceiling is the configured `MAX_UPLOAD_BYTES`, rendered from the server's own answer rather than from a literal in a mock | -- corrected |
 * | `Download template` | The template is the master workbook itself, which Atlas does not hold a copy of and must not pretend to | -- not planned |
 * | `6 today / 9 / 39 / 70` | Read from the most recent staged plan rather than hardcoded. Empty until a batch has been parsed, because adr-16 seeds no hierarchy | -- |
 * | Step 4, *"Commit"* | Drawn as the step it is, and described as not yet available. The explainer is a map of the process, not a set of buttons | f8.3 |
 *
 * Every colour is a token and every row is bound to the server's answer.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  FileSpreadsheet,
  Info,
  ShieldAlert,
  UploadCloud,
} from 'lucide-react';

import { ApiError, type IngestionBatchSummary } from '@/api/client';
import {
  useIngestionBatches,
  useJobProgress,
  useSettingsCatalog,
  useUploadBatch,
} from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';
import { EmptyCell } from '@/ui/EmptyCell';
import {
  batchLabel,
  formatWhen,
  isOpen,
  outcomeFor,
} from './importView';

/** The feed this screen uploads. The server routes it to `master.import`. */
const SOURCE_SYSTEM = 'MASTER_WORKBOOK';

/** How many batches the table shows. The prototype draws five. */
const RECENT_LIMIT = 5;

/**
 * The sheet and columns the parser binds to, stated on the page.
 *
 * **Nine, and no `SAP ID`.** Column order does not matter -- the binding is by
 * header name -- and a missing required column stops the upload before
 * anything is staged. Both of those are enforced server-side; the page states
 * them so an operator preparing a file knows before they upload.
 */
const EXPECTED_COLUMNS: readonly { name: string; required: boolean }[] = [
  { name: 'Business Unit Group', required: true },
  { name: 'Business Unit', required: true },
  { name: 'Client', required: true },
  { name: 'Engagement Name', required: true },
  { name: 'DEM', required: false },
  { name: 'CEM', required: false },
  { name: 'CM', required: false },
  { name: 'Project Head', required: false },
  { name: 'DM', required: false },
];

/** The four steps an import runs through. Step 4 is f8.3's. */
const STEPS: readonly { title: string; meta: string; available: boolean }[] = [
  {
    title: 'Upload the workbook',
    meta: 'Format, sheets and columns are checked before anything is staged',
    available: true,
  },
  {
    title: 'Review the staged data',
    meta: 'What will be created, updated and left unchanged, per level',
    available: true,
  },
  {
    title: 'Resolve role holders',
    meta: 'Every DEM, CEM, CM, DM and Project Head bound to a user',
    available: false,
  },
  {
    title: 'Commit',
    meta: 'Written in one transaction and recorded in the audit log',
    available: false,
  },
];

function PageHead() {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">Data import</h1>
        <div className="page-sub">
          The master workbook, parsed and staged for review &mdash; nothing is written
          to Atlas until a batch is committed
        </div>
      </div>
    </div>
  );
}

/** The setting that bounds an upload. Read, never restated as a literal. */
const UPLOAD_LIMIT_SETTING = 'ingestion.max_upload_size_mb';

/**
 * The configured ceiling, from the settings catalogue.
 *
 * `import.html` writes *"up to 25 MB"*. That is prototype text: the ceiling is
 * a setting an Admin can change, and a screen that renders a literal instead
 * would be wrong the first time somebody does.
 */
function useUploadLimit(): string | null {
  const { data } = useSettingsCatalog();
  const setting = data?.sections
    .flatMap((section) => section.settings)
    .find((entry) => entry.key === UPLOAD_LIMIT_SETTING);
  return setting ? `${String(setting.value)} MB` : null;
}

function Dropzone({
  limit,
  onFile,
  busy,
}: {
  limit: string | null;
  onFile: (file: File) => void;
  busy: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const take = useCallback(
    (files: FileList | null) => {
      const file = files?.item(0);
      if (file) onFile(file);
    },
    [onFile],
  );

  return (
    <div
      className={`dropzone${over ? ' is-over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        take(event.dataTransfer.files);
      }}
    >
      <UploadCloud className="dz-icon" aria-hidden="true" />
      <div className="dz-title">Drop the workbook here</div>
      <div className="hint">
        The file is parsed and staged for review &mdash; nothing is written to Atlas on
        upload.
      </div>
      <div className="btn-row dz-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          <FileSpreadsheet aria-hidden="true" /> Choose file
        </button>
      </div>
      <div className="hint">
        The format is detected from the file&rsquo;s content, never from its name
        {limit ? ` · up to ${limit}` : ''}
      </div>
      <input
        ref={input}
        type="file"
        className="sr-only"
        aria-label="Master workbook"
        onChange={(event) => {
          take(event.target.files);
          event.target.value = '';
        }}
      />
    </div>
  );
}

function RecentBatches({
  batches,
  loading,
}: {
  batches: readonly IngestionBatchSummary[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="card-body" aria-busy="true" aria-label="Loading recent imports">
        {[0, 1, 2].map((index) => (
          <span key={index} className="skeleton-bar group-skeleton-line" />
        ))}
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="card-body">
        <div className="empty">
          <div className="e-icon">
            <UploadCloud aria-hidden="true" />
          </div>
          <h3>No imports yet</h3>
          <p>
            The hierarchy ships empty by design. Upload the master workbook to see what
            it would create.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-body table-scroll">
      <table className="data compact">
        <thead>
          <tr>
            <th>Batch</th>
            <th>File</th>
            <th>Uploaded by</th>
            <th>When</th>
            <th className="num">Rows</th>
            <th>Outcome</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {batches.map((batch) => {
            const outcome = outcomeFor(batch);
            return (
              <tr key={batch.id}>
                <td className="mono">{batchLabel(batch.batch_number)}</td>
                <td className="truncate">
                  {batch.original_filename ?? <EmptyCell />}
                </td>
                <td>{batch.uploaded_by_name ?? <EmptyCell />}</td>
                <td>{formatWhen(batch.uploaded_at)}</td>
                <td className="num">
                  {batch.rows_in === null ? <EmptyCell /> : batch.rows_in}
                </td>
                <td>
                  <span className={`badge ${outcome.pill}`}>{outcome.label}</span>
                  {outcome.detail ? (
                    <span className="sub">{outcome.detail}</span>
                  ) : null}
                </td>
                <td>
                  <Link
                    className="btn btn-secondary btn-xs"
                    to={`/data-import/${batch.id}`}
                  >
                    {isOpen(batch.state) ? 'Review' : 'Open'}
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DataImportPage() {
  const navigate = useNavigate();
  const limit = useUploadLimit();
  const batches = useIngestionBatches({
    source_system: SOURCE_SYSTEM,
    limit: RECENT_LIMIT,
  });
  const upload = useUploadBatch();
  const [watching, setWatching] = useState<{ batchId: string; jobId: string } | null>(
    null,
  );
  const job = useJobProgress(watching?.jobId);

  // The redirect the prototype's "Open the staged batch" stands for: once the
  // parse has finished there is something to review, and the operator is taken
  // to it rather than left to find it in the table. In an effect rather than
  // in render, because navigating during render is a side effect React is
  // entitled to run twice.
  const finishedJobState = job.data?.state;
  const watchedBatchId = watching?.batchId;
  useEffect(() => {
    if (finishedJobState === 'SUCCEEDED' && watchedBatchId) {
      setWatching(null);
      void navigate(`/data-import/${watchedBatchId}`);
    }
  }, [finishedJobState, watchedBatchId, navigate]);

  if (batches.error instanceof ApiError && batches.error.status === 403) {
    return (
      <>
        <PageHead />
        <div className="card">
          <div className="card-body">
            <div className="empty">
              <div className="e-icon e-icon-warning">
                <ShieldAlert aria-hidden="true" />
              </div>
              <h3>No access</h3>
              <p>
                The server refuses this area for your account, and records the refusal
                in the audit log. Data import is administrators only.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  const open = (batches.data?.batches ?? []).filter((batch) => isOpen(batch.state));
  // Two batches may legitimately be staged at once -- the prototype draws the
  // banner for it -- and the newest is the one the operator just uploaded.
  const oldestOpen = open.at(0);

  return (
    <>
      <PageHead />

      {oldestOpen ? (
        <div className="alert alert-warning mb-4">
          <AlertTriangle aria-hidden="true" />
          <div>
            <div className="a-title">
              {open.length === 1
                ? 'One batch is staged and not yet committed'
                : `${open.length} batches are staged and not yet committed`}
            </div>
            <div className="a-body">
              Nothing is written until a batch is committed.{' '}
              <Link className="cell-link" to={`/data-import/${oldestOpen.id}`}>
                Open the staged batch
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {upload.error instanceof ApiError ? (
        <div className="alert alert-danger mb-4">
          <AlertTriangle aria-hidden="true" />
          <div>
            <div className="a-title">The upload was refused</div>
            <div className="a-body">{upload.error.message}</div>
          </div>
        </div>
      ) : null}

      <div className="split-2-1">
        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>Upload the master workbook</h3>
              {limit ? <span className="hint">XLSX &middot; up to {limit}</span> : null}
            </div>
            <div className="card-body">
              <Dropzone
                limit={limit}
                busy={upload.isPending || Boolean(watching)}
                onFile={(file) =>
                  upload.mutate(
                    { file, sourceSystem: SOURCE_SYSTEM },
                    {
                      onSuccess: (accepted) =>
                        setWatching({
                          batchId: accepted.batch_id,
                          jobId: accepted.job_id,
                        }),
                    },
                  )
                }
              />

              {job.data ? (
                <div className="mt-6">
                  <JobProgress job={job.data} title="Parsing the workbook" />
                </div>
              ) : null}

              <div className="mt-6">
                <div className="section-title">Expected sheets and columns</div>
                <div className="chip-row">
                  <span className="badge badge-chip">Sheet: US Projects</span>
                  {EXPECTED_COLUMNS.map((column) => (
                    <span
                      key={column.name}
                      className={`badge ${
                        column.required ? 'badge-primary' : 'badge-outline'
                      }`}
                    >
                      {column.name}
                    </span>
                  ))}
                </div>
                <div className="chip-row mt-4">
                  <span className="badge badge-chip">Sheet: OR_Q_July 22</span>
                  <span className="hint">
                    Engagement attributes and the Kimble code, joined on Engagement
                    Name. Enrichment only &mdash; it adds no engagement.
                  </span>
                </div>
                <div className="chip-row mt-4">
                  <span className="badge badge-chip">Sheet: TM</span>
                  <span className="hint">
                    The <code>Last, First</code> name crosswalk. It has no header row
                    and creates no user.
                  </span>
                </div>
                <div className="hint mt-4">
                  The four highlighted columns are required. Column order does not
                  matter &mdash; columns bind by header name &mdash; and a missing
                  required column stops the upload before anything is staged.
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Recent imports</h3>
              <span className="hint">Last {RECENT_LIMIT} batches</span>
            </div>
            <RecentBatches
              batches={batches.data?.batches ?? []}
              loading={batches.isLoading}
            />
          </div>
        </div>

        <div className="stack">
          <div className="card">
            <div className="card-head">
              <h3>How an import runs</h3>
            </div>
            <div className="card-body">
              <div className="step-rail">
                {STEPS.map((step, index) => (
                  <div
                    key={step.title}
                    className={`step${step.available ? '' : ' is-todo'}`}
                  >
                    <span className="step-num">{index + 1}</span>
                    <span className="step-body">
                      <span className="step-title">{step.title}</span>
                      <span className="step-meta">
                        {step.meta}
                        {step.available ? '' : ' — not available yet'}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>What an import touches</h3>
            </div>
            <div className="card-body">
              <div className="alert alert-neutral">
                <Info aria-hidden="true" />
                <div className="a-body">
                  Existing records are matched by name, exactly, after case and spacing
                  are folded. Records missing from the workbook are{' '}
                  <strong>never deleted</strong> &mdash; deactivate them from their own
                  list.
                </div>
              </div>
              <div className="hint mt-4">
                An import proposes Business Unit Groups, Business Units, Clients and
                Engagements, and the role holders that own them. Open a staged batch to
                see exactly what it would create.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
